import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AnalysisManagementService } from '../../services/analysis-management.service';
import { AnalysisSequenceItem } from "../sequnce-models/analysisSequenceItem";
import { MapsTypesAvailabilityDto } from '../../dto/MapsTypesAvailabilityDto';
import { MapsTypesAvailabilityModel } from '../../models/mapsTypesAvailabilityModel';
import { AvailableMapsFieldModel } from '../../models/availableMapsFieldModel';
import dayjs from "dayjs";
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { MapSyncService } from '../map-sequence-services/map-sync.service';
import { CustomSequenceService } from '../map-sequence-services/custom-sequence.service';
import { CustomSequenceSelectionComponent } from '../custom-sequence-selection/custom-sequence-selection.component';

@Component({
  selector: 'app-custom-analysis-sequence',
  standalone: false,
  templateUrl: './custom-analysis-sequence.component.html',
  styleUrl: './custom-analysis-sequence.component.css',
})
export class CustomAnalysisSequenceComponent implements OnInit, OnDestroy {
  @Output() sequenceViewClosed = new EventEmitter<void>();

  protected readonly dayjs = dayjs;
  currentDate = dayjs().format('YYYY-MM-DD');

  analysisSequenceList: AnalysisSequenceItem[] = [];
  analysisTypesAvailable: MapsTypesAvailabilityModel[] = [];
  selectedSequenceItems: AvailableMapsFieldModel[] = [];
  cards = [];

  fieldId: number = null;
  analysisCount: number = 10;
  gridSize: number = 2;

  isNoItemsFound: boolean = false;
  showLegends: boolean = false;

  fromDate: string = '';
  toDate: string = '';
  filterFromDate: string = '';
  filterToDate: string = '';
  currentFieldName: string = '';
  currentFieldSize: string = '';

  constructor(
    private analysisManagementService: AnalysisManagementService,
    private route: ActivatedRoute,
    public router: Router,
    public nav: NavBarComponent,
    private mapSyncService: MapSyncService,
    private customSequenceService: CustomSequenceService,
    private dialog: MatDialog,
    private location: Location,
  ) {}

  zoomIn(): void {
    this.mapSyncService.zoomAll(0.5);
  }

  zoomOut(): void {
    this.mapSyncService.zoomAll(-0.5);
  }

  resetMaps(): void {
    this.mapSyncService.resetAll();
  }

  clearAllSelectedSequenceCards(): void {
    this.selectedSequenceItems = [];
    this.cards = [];
    this.isNoItemsFound = true;
    this.customSequenceService.clearSelectedItems();
    this.mapSyncService.resetAll();
  }

  toggleLegends(): void {
    this.mapSyncService.toggleLegends();
  }

  ngOnInit(): void {
    this.getFieldInfo();
    this.mapSyncService.resetLegends();
    this.showLegends = false;
    this.restoreSelection();

    this.fromDate = dayjs().subtract(5, 'year').format('YYYY-MM-DD');
    this.toDate = dayjs().format('YYYY-MM-DD');
    this.filterFromDate = this.fromDate;
    this.filterToDate = this.toDate;

    try {
      const fieldIdParam = this.route.snapshot.paramMap.get('fieldId');
      this.fieldId = fieldIdParam ? +fieldIdParam : 0;

      if (this.fieldId > 0) {
        this.populateAvailableTypes();
      } else {
        console.error('Invalid fieldId');
        this.isNoItemsFound = true;
      }
    } catch (error) {
      console.error('Error initializing analysis sequence:', error);
      this.isNoItemsFound = true;
    }
  }

populateAvailableTypes(): void {
  const dto: MapsTypesAvailabilityDto = { farmId: null, fieldId: this.fieldId };
  this.analysisManagementService.getMapsTypesAvailability(dto).subscribe({
    next: (availableTypes) => {
      this.analysisTypesAvailable = availableTypes.map(t => ({
        type: t.type,
        available: t.type.toLowerCase().includes('profitability') ? true : t.available,
        alias: t.alias,
        displayOrder: 1,
        selected: false
      }));
    },
    error: () => this.nav.toggleLoading(false),
    complete: () => this.nav.toggleLoading(false),
  });
}


  openSelectItemsDialog(): void {
    const dialogRef = this.dialog.open(CustomSequenceSelectionComponent, {
      data: {
        fieldId: this.fieldId,
        analysisTypes: this.analysisTypesAvailable,
        previousSelectedItems: this.customSequenceService.getSelectedItems(),
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.selectedItems) {
        this.selectedSequenceItems = result.selectedItems;
        this.customSequenceService.setSelectedItems(result.selectedItems);
        this.buildCardsFromSelection();
      }
    });
  }

  removeSelectedItem(item: AvailableMapsFieldModel): void {
    this.selectedSequenceItems = this.selectedSequenceItems.filter(
      s => !(s.analysisId === item.analysisId && s.items[0]?.propertyTypeId === item.items[0]?.propertyTypeId)
    );
    this.customSequenceService.setSelectedItems(this.selectedSequenceItems);
    this.mapSyncService.resetAll();
    this.buildCardsFromSelection();
  }

  private restoreSelection(): void {
    const saved = this.customSequenceService.getSelectedItems();
    if (saved.length > 0) {
      this.selectedSequenceItems = saved;
      this.buildCardsFromSelection();
    }
  }

  buildCardsFromSelection(): void {
    if (this.selectedSequenceItems.length === 0) {
      this.cards = [];
      this.isNoItemsFound = true;
      return;
    }

    this.isNoItemsFound = false;
    this.cards = this.selectedSequenceItems.map(item => ({
      title: (item.type || 'Unknown') + (!item.type?.toLowerCase().includes('satellite') && !item.type?.toLowerCase().includes('topography') && item.items?.[0]?.propertyTypeName ? ' | ' + item.items[0].propertyTypeName : ''),
      content: item.datePerformed || '',
      fieldId: this.fieldId,
      analysisId: item.analysisId,
      propertyTypeId: item.items?.[0]?.propertyTypeId,
      mapUrl: item.items?.[0]?.mapUrl || '',
      latMin: item.items?.[0]?.latMin,
      lngMin: item.items?.[0]?.lngMin,
      latMax: item.items?.[0]?.latMax,
      lngMax: item.items?.[0]?.lngMax,
    }));
  }

  ngOnDestroy(): void {
    this.customSequenceService.clearSelectedItems();
    this.mapSyncService.resetAll();
  }

  closeSequenceView(): void {
    try {
      this.sequenceViewClosed.emit();
      this.location.back();
    } catch (error) {
      console.error('Error closing sequence view:', error);
      this.router.navigate(['field']);
    }
  }

  setFilterStartDate(value: string | Date): void {
    this.fromDate = value ? dayjs(value).format('YYYY-MM-DD') : '';
  }

  setFilterEndDate(value: string | Date): void {
    this.toDate = value ? dayjs(value).format('YYYY-MM-DD') : '';
  }

  formatDate(date: string, format: string): string {
    return dayjs(date).format(format);
  }

  getFieldInfo(): void {
    const fieldData = sessionStorage.getItem('currentFieldInfo');
    let fieldObj = JSON.parse(fieldData);
    this.currentFieldName = fieldObj?.fieldName;
    this.currentFieldSize = fieldObj?.fieldSize;
  }
}
